import type { ReactNode } from 'react';
import attentionIcon from '../../assets/svgs/attention-icon.svg';
import CustomButton from '../customButton/CustomButton';
import {
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from './Dialog';

type ActionDialogProps = {
    open: boolean;
    onCancel: () => void;
    onConfirm?: () => void;
    title: ReactNode;
    description: ReactNode;
    iconSrc?: string;
    closeLabel?: string;
    showCancelButton?: boolean;
    confirmButtonText?: string;
    cancelButtonText?: string;
    confirmHref?: string;
    confirmExternal?: boolean;
};

export default function ActionDialog({
    open,
    onCancel,
    onConfirm,
    title,
    description,
    iconSrc = attentionIcon,
    closeLabel = 'Close',
    showCancelButton = false,
    confirmButtonText = 'OK',
    cancelButtonText = 'Cancel',
    confirmHref,
    confirmExternal = false,
}: ActionDialogProps) {
    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            onCancel();
        }
    };

    const handleConfirm = () => {
        onConfirm?.();
        if (!confirmHref) {
            onCancel();
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                aria-describedby={undefined}
                className='max-w-[90%] gap-8 p-6 lg:max-w-[28rem] lg:gap-10 '
            >
                <DialogCloseButton className='right-6 top-6 h-6 w-6' closeLabel={closeLabel} />
                <div className='flex flex-col items-center gap-2 text-center mt-8'>
                    <img src={iconSrc} alt='' className='h-20 w-20' />
                    <DialogTitle className='text-2xl !leading-relaxed font-medium mt-6 text-custom-gray-800'>
                        {title}
                    </DialogTitle>
                    <DialogDescription className='text-base !leading-relaxed text-custom-gray-800'>
                        {description}
                    </DialogDescription>
                </div>
                <div className='flex items-center justify-center gap-6'>
                    {showCancelButton ? (
                        <CustomButton variant='outlined' onClick={onCancel} className='max-w-52'>
                            {cancelButtonText}
                        </CustomButton>
                    ) : null}
                    {confirmHref ? (
                        <a
                            href={confirmHref}
                            target={confirmExternal ? '_blank' : undefined}
                            rel={confirmExternal ? 'noopener noreferrer' : undefined}
                            onClick={handleConfirm}
                            className='inline-flex h-12 w-full max-w-52 items-center justify-center rounded-lg border border-primary bg-primary px-10 py-3 text-base font-semibold leading-relaxed text-white transition'
                        >
                            {confirmButtonText}
                        </a>
                    ) : (
                        <CustomButton onClick={handleConfirm} className='w-full'>
                            {confirmButtonText}
                        </CustomButton>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
